import { useState, useEffect } from "react";
import RightTimeApi from "@/API";
import { FaPlusSquare } from "react-icons/fa";
import { IoTrashOutline } from "react-icons/io5";
import { CiEdit } from "react-icons/ci";
import { RiSave3Fill } from "react-icons/ri";
import { ImCancelCircle } from "react-icons/im";
import { MdCheckBoxOutlineBlank, MdCheckBox } from "react-icons/md";
import Enums from "@/enums";
import { toast } from "react-toastify";
import ToolTip from "../ToolTip";
import { FaExclamationCircle } from "react-icons/fa";
import { apiMessage } from "@/utils/apiMessage";
import { CANNOT_DELETE_ROLE_MESSAGE, roleCanBeDeleted } from "@/utils/roleProtection";

export default function CompanyRolesTable(props: any) {
  const { updateSelectedRole, updateSpinner, activeTab, updateData, selectedRole, instructions, roles, company, adminPerms } = props;
  const canEditRoles = adminPerms?.includes(Enums.ROLE_PERMS.EditRoles)
    || adminPerms?.includes(Enums.ROLE_PERMS.CompanyAdmin)
    || adminPerms?.includes(Enums.ROLE_PERMS.SystemAdmin);
  const canDeleteRoles = adminPerms?.includes(Enums.ROLE_PERMS.DeleteRole)
    || adminPerms?.includes(Enums.ROLE_PERMS.CompanyAdmin)
    || adminPerms?.includes(Enums.ROLE_PERMS.SystemAdmin);
  const [editingRole, updateEditingRole] = useState<any>({});
  const [localRoles, updateRoles] = useState<any[]>([]);
  const [showAllRoles, setShowAllRoles] = useState(false);

  useEffect(() => {
    updateRoles((prevLocal) => {
      const draft = prevLocal.find((r) => r.id === 0);
      if (draft) {
        return [draft, ...roles];
      }
      return roles;
    });
  }, [roles]);

  function isRoleActive(role: any) {
    return role?.active !== false;
  }

  const listedRoles = localRoles.filter((role) => showAllRoles || isRoleActive(role));

  if (!isOnThisTab()) return null;

  function isOnThisTab() {
    return props && activeTab && activeTab === "roles";
  }
  function startRoleEdit(e) {
    if (!canEditRoles) return;
    e.stopPropagation();
    let id = parseInt(e.currentTarget.getAttribute("data-id"));
    var roleData = localRoles.find((r) => r.id === id);
    if (roleData) {
      updateEditingRole(roleData);
    }
  }
  function startCreateRole() {
    if (!canEditRoles) return;
    const newRole = { name: "", id: 0, active: true };
    updateEditingRole(newRole);
    updateRoles([newRole, ...localRoles]);
    updateSelectedRole(newRole);
  }

  function isRtUser() {
    return company?.permissions?.some(
      (p) => p.companyId === 1 && p.permissionId === Enums.ROLE_PERMS.CompanyRolesTable
    ) ?? false;
  }



  async function saveRoleEditAsync(e) {
    editingRole.companyId = company?.id;
    let resp = await RightTimeApi.sendAsync("/CompanyRole", editingRole);
    if (resp && resp.ok) {
      updateEditingRole(null);
      toast.success("Company role " + editingRole.name + " saved.");
      updateData();
    }
  }

  async function onCancelEditAsync(e) {
    if (editingRole && editingRole.id === 0)
      updateRoles(localRoles.filter((r) => r.id !== 0));
    updateEditingRole(null);
  }

  async function onChangeRoleAsync(e) {
    var field = e.target.getAttribute("data-field");
    var id = parseInt(e.target.getAttribute("data-id"));
    var value = e.target.getAttribute("data-value") || e.target.value;
    var datatype = e.target.getAttribute("data-type");
    switch (datatype) {
      case "bool":
        value = value === "1" || value === "true" ? true : false;
        break;
      case "int":
        if(value=="" || value=="0")
          value=null;
        else
          value = parseInt(value);
        break;
    }

    if (editingRole && editingRole.id != id) return;
    updateEditingRole({ ...editingRole, [field]: value });
  }

  async function onChangeSelectionAsync(e) {
    if (editingRole && editingRole.id >= 0) return;
    let id = parseInt(e.currentTarget.getAttribute("data-id"));
    const role = localRoles.find((r) => r.id === id);
    updateSelectedRole(role);
    onCancelEditAsync(e);
  }

  async function deleteRoleAsync(role) {
    if (!canDeleteRoles || !roleCanBeDeleted(role)) return;
    if (window.confirm("Are you sure you want to delete this role?")) {
      const resp = await RightTimeApi.deleteAsync("/CompanyRole/" + role.id);
      if (resp && resp.ok) {
        toast.success("Role deleted.");
        updateData();
      } else {
        toast.error(apiMessage(resp, CANNOT_DELETE_ROLE_MESSAGE));
      }
    }
  }

  async function toggleActiveAsync(e, role) {
    if (!canEditRoles || !roleCanBeDeleted(role) || !role || role.id <= 0) return;
    e.stopPropagation();

    const nextActive = !isRoleActive(role);
    const resp = await RightTimeApi.putAsync(`/CompanyRole/${role.id}/Active`, { active: nextActive });
    if (resp?.ok) {
      toast.success(nextActive ? `Role "${role.name}" activated.` : `Role "${role.name}" deactivated.`);
      updateData();
    } else {
      toast.error(apiMessage(resp, CANNOT_DELETE_ROLE_MESSAGE));
    }
  }

  return (
    <div className="tableWrapper dimensionTable horizontal scrollable companyRolesTable" style={{ maxHeight: "600px" }}>
      <table>
        <caption>
          <span className="title">Company Roles</span>
          <span className="captionRight">
            <FaPlusSquare
              className="actionIcon plusIcon addRole"
              onClick={startCreateRole}
            />
          </span>
        </caption>
        <thead>
          <tr>
            <th>  </th>
            <th
              className="toggleColumnHeader clickable"
              data-help-id="toggleActive"
              onClick={() => setShowAllRoles(!showAllRoles)}
              title={showAllRoles ? "Showing all roles — click to show active only" : "Showing active roles — click to show all"}
            >
              {showAllRoles ? "All" : "Active"}
            </th>
            <th>Name</th>
              <th>Recents<br />Instructions</th>
              <th></th>
          </tr>
        </thead>
        <tbody>
          {listedRoles &&
            listedRoles.map((r) => {
              // console.log(["checking", editingRole, r]);
              let isChecked =
                editingRole && editingRole.id == r.id
                  ? editingRole.systemOnly
                  : r.systemOnly;
              const canRemoveThisRole = canDeleteRoles && roleCanBeDeleted(r);
              const canToggleThisRole = canEditRoles && roleCanBeDeleted(r);
              const protectedRole = !roleCanBeDeleted(r);
              const toggleDisabledTitle = protectedRole
                ? CANNOT_DELETE_ROLE_MESSAGE
                : "You don't have permission to edit roles";
              const deleteDisabledTitle = protectedRole
                ? CANNOT_DELETE_ROLE_MESSAGE
                : "You don't have permission to delete roles";
              return (
                <tr
                  key={"r" + r.id}
                  data-id={r.id}
                  className={`${selectedRole?.id == r.id ? "active" : ""}${isRoleActive(r) ? "" : " inactive"}`.trim()}
                  onClick={onChangeSelectionAsync}
                >
                  <td>{r.id}</td>
                  <td
                    className="actions center"
                    onClick={(e) => toggleActiveAsync(e, r)}
                    title={canToggleThisRole ? undefined : toggleDisabledTitle}
                  >
                    {isRoleActive(r) ? (
                      <MdCheckBox className={`checkbox checked actionIcon${canToggleThisRole ? "" : " disabled"}`} />
                    ) : (
                      <MdCheckBoxOutlineBlank className={`checkbox unchecked actionIcon${canToggleThisRole ? "" : " disabled"}`} />
                    )}
                  </td>
                  {editingRole && editingRole.id === r.id ? (
                    <td>
                      <input
                        value={editingRole.name}
                        data-id={editingRole.id}
                        data-field="name"
                        onChange={onChangeRoleAsync}
                        autoFocus
                        className="inlineEdit nameEdit"
                      />
                    </td>
                  ) : (
                    <td>{r.name}</td>
                  )}
                  <td>
                    {editingRole && editingRole.id === r.id ? (
                      <select
                        value={editingRole.instructionsId}
                        data-id={editingRole.id}
                        data-field="instructionsId"
                        data-type="int"
                        className="instructionsSelect"
                        onChange={onChangeRoleAsync}>
                        <option value="">None</option>
                        {(instructions ?? []).map((i) => (
                          <option key={i.key} value={i.key}>
                            {i.value}
                          </option>
                        ))}
                      </select>
                    ) : r.instructions || ""}
                  </td>
                  <td className="actions right">
                    {editingRole && editingRole.id === r.id ? (
                      <>
                        <RiSave3Fill
                          className="save actionIcon"
                          data-id={r.id}
                          onClick={saveRoleEditAsync}
                          title="Save Changes"
                        />
                        <ImCancelCircle
                          className="cancel actionIcon"
                          data-id={r.id}
                          onClick={onCancelEditAsync}
                          title="Cancel Changes"
                        />
                      </>
                    ) : (
                      <>
                        {r.warnings && r.warnings.length > 0 && (
                          <ToolTip text={r.warnings.join("\n")}>
                            <FaExclamationCircle className="exclamationIcon" />
                          </ToolTip>
                        )}
                        <CiEdit
                          className={`actionIcon editIcon${canEditRoles ? "" : " disabled"}`}
                          data-id={r.id}
                          onClick={(e) => {
                            if (canEditRoles) startRoleEdit(e);
                          }}
                          title={canEditRoles ? "Edit Role" : "You don't have permission to edit roles"}
                        />
                        <IoTrashOutline
                          className={`actionIcon deleteIcon${canRemoveThisRole ? "" : " disabled"}`}
                          onClick={() => {
                            if (canRemoveThisRole) deleteRoleAsync(r);
                          }}
                          title={canRemoveThisRole ? "Delete Role" : deleteDisabledTitle}
                        />
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
        </tbody>
      </table>
    </div>
  );
}

